/**
 * One timeline for a chapter, merging what the server accepted with what this
 * device tried to send.
 *
 * The server only knows about writes it processed. The local journal is the
 * only record of a save that was rejected or never delivered, so the panel has
 * to show both or the author cannot tell "lost" apart from "never left here".
 */

import { listChapterParagraphs, type ChapterDocContent } from './chapterParagraphs';
import type { ChapterJournalEntry } from './chapterSync/types';
import { isOverwriteAnnotationPayload, type OverwriteRegion } from './overwriteAnnotations';

export type ChapterTimelineOutcome = 'saved' | 'overwritten' | 'rejected' | 'undelivered' | 'local';

export type ChapterTimelineItem = {
  id: string;
  source: 'server' | 'local';
  kind: string;
  outcome: ChapterTimelineOutcome;
  outcomeLabel: string;
  summary: string;
  preview: string | null;
  wordCount: number | null;
  at: string;
  atMs: number;
  userLabel?: string | null;
  /** Server rows restore through the API; local rows load their stored body. */
  restorable: boolean;
  /** Body carried by a local entry, when retention has not pruned it. */
  contentJson?: string;
  title?: string;
  overwriteRegions?: OverwriteRegion[] | null;
};

export type ServerHistoryRow = {
  id: string;
  kind: string;
  summary: string | null;
  wordCount: number | null;
  createdAt: string;
  user?: { name?: string | null; email?: string | null } | null;
  hasSnapshot?: boolean;
  preview?: string | null;
  annotations?: unknown;
};

const OUTCOME_LABELS: Record<ChapterTimelineOutcome, string> = {
  saved: 'Saved',
  overwritten: 'Overwritten',
  rejected: 'Rejected — not saved',
  undelivered: 'Not yet delivered',
  local: 'On this device',
};

export function outcomeLabel(outcome: ChapterTimelineOutcome): string {
  return OUTCOME_LABELS[outcome];
}

export function serverKindOutcome(kind: string): ChapterTimelineOutcome {
  if (kind === 'conflict') return 'rejected';
  if (kind === 'failed') return 'rejected';
  if (kind === 'overwrite' || kind === 'replaced') return 'overwritten';
  return 'saved';
}

export function journalKindOutcome(kind: ChapterJournalEntry['kind']): ChapterTimelineOutcome {
  switch (kind) {
    case 'push_ok':
      return 'saved';
    case 'push_failed':
      return 'rejected';
    case 'offline':
      return 'undelivered';
    default:
      return 'local';
  }
}

function previewFromContentJson(contentJson: string | undefined, max = 160): string | null {
  if (!contentJson) return null;
  try {
    const doc = JSON.parse(contentJson) as ChapterDocContent;
    const text = listChapterParagraphs(doc)
      .map((p) => p.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return null;
    return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
  } catch {
    return null;
  }
}

function journalSummary(entry: ChapterJournalEntry): string {
  switch (entry.kind) {
    case 'push_ok':
      return `Saved to your library${entry.rev != null ? ` (rev ${entry.rev})` : ''}`;
    case 'push_failed':
      return entry.error ? `Save failed — ${entry.error}` : 'Save failed';
    case 'offline':
      return 'Saved on this device, waiting to reach your library';
    default:
      return 'Typed on this device';
  }
}

function toLocalItem(entry: ChapterJournalEntry): ChapterTimelineItem {
  const outcome = journalKindOutcome(entry.kind);
  return {
    id: `local:${entry.key}`,
    source: 'local',
    kind: entry.kind,
    outcome,
    outcomeLabel: outcomeLabel(outcome),
    summary: journalSummary(entry),
    preview: previewFromContentJson(entry.contentJson),
    wordCount: entry.wordCount ?? null,
    at: new Date(entry.at).toISOString(),
    atMs: entry.at,
    restorable: Boolean(entry.contentJson),
    contentJson: entry.contentJson,
    title: entry.title,
  };
}

function toServerItem(row: ServerHistoryRow): ChapterTimelineItem {
  const outcome = serverKindOutcome(row.kind);
  const atMs = new Date(row.createdAt).getTime();
  return {
    id: `server:${row.id}`,
    source: 'server',
    kind: row.kind,
    outcome,
    outcomeLabel: outcomeLabel(outcome),
    summary: row.summary || 'Edit',
    preview: row.preview ?? null,
    wordCount: row.wordCount,
    at: row.createdAt,
    atMs: Number.isFinite(atMs) ? atMs : 0,
    userLabel: row.user?.name || row.user?.email || null,
    restorable: Boolean(row.hasSnapshot),
    overwriteRegions: isOverwriteAnnotationPayload(row.annotations)
      ? row.annotations.regions
      : null,
  };
}

/**
 * Newest first. Local `flush` entries are dropped when the server already has a
 * row within `coalesceWindowMs` — otherwise every autosave shows up twice.
 * Anything that failed or never shipped is always kept.
 */
export function mergeChapterTimeline(
  serverRows: ServerHistoryRow[],
  journal: ChapterJournalEntry[],
  options?: { coalesceWindowMs?: number; limit?: number }
): ChapterTimelineItem[] {
  const coalesceWindowMs = options?.coalesceWindowMs ?? 5000;
  const server = serverRows.map(toServerItem);
  const serverTimes = server.map((s) => s.atMs).sort((a, b) => a - b);

  const local = journal
    .filter((entry) => {
      if (entry.kind !== 'flush' && entry.kind !== 'push_ok') return true;
      return !serverTimes.some((t) => Math.abs(t - entry.at) <= coalesceWindowMs);
    })
    .map(toLocalItem);

  const merged = [...server, ...local].sort((a, b) => b.atMs - a.atMs);
  return typeof options?.limit === 'number' ? merged.slice(0, options.limit) : merged;
}

/** Server rows this timeline treats as "your work did not land". */
export function hasUnsavedAttempts(items: ChapterTimelineItem[]): boolean {
  return items.some((i) => i.outcome === 'rejected' || i.outcome === 'undelivered');
}
